'use client';

import type { ReactNode } from 'react';
import { useLocale } from '@akademi/site-builder-runtime';
import { getMessages, message } from '@/src/lib/messages';
import type { RestaurantSiteState } from '@/src/lib/hospitality/useRestaurantSite';

/**
 * The pieces every restaurant block shares: strings, a section wrapper, and one
 * place that decides what a diner sees when the data is not there.
 *
 * <p>No `<main>` and no chrome anywhere in this folder. The operator's Shell
 * provides both, and a block that renders its own header is a block that cannot
 * be dropped onto a page next to another one.
 */

/** UI strings for the active locale, from this app's own catalogues. The blocks
 *  do not use `react-i18next`: no `I18nextProvider` is mounted in this host, and
 *  the app's `src/locales/*.json` is where its own words already live. */
export function useHospitalityMessages(): (
  key: string,
  params?: Record<string, string | number>,
) => string {
  const { locale, fallback } = useLocale();
  const messages = getMessages(locale, fallback);
  return (key, params) => message(messages, key, params);
}

/** The active BCP 47 tag, for `Intl`. Never defaulted to a language here — the
 *  tenant's own locale set decides, and it arrives through `LocaleContext`. */
export function useActiveLocale(): string {
  const { locale, fallback } = useLocale();
  return locale || fallback;
}

export function BlockSection({
  heading,
  headingLevel = 'h2',
  children,
}: {
  heading?: string;
  headingLevel?: 'h1' | 'h2' | 'h3';
  children: ReactNode;
}) {
  const Heading = headingLevel;
  return (
    <section className="mx-auto w-full max-w-5xl px-4 py-8">
      {heading ? <Heading className="text-2xl font-semibold">{heading}</Heading> : null}
      <div className={heading ? 'mt-4' : undefined}>{children}</div>
    </section>
  );
}

/** A neutral note. Used for every "nothing to show" case, including the ones
 *  caused by a backend gap — a diner is told the feature is not available here,
 *  never given an endpoint name. */
export function EmptyNote({ children }: { children: ReactNode }) {
  return <p className="text-sm text-[var(--color-text-secondary)]">{children}</p>;
}

/**
 * Renders the non-ready states of {@link RestaurantSiteState}, or `null` when
 * the site is loaded and the block should render itself.
 *
 * <p><strong>Never renders another tenant's anything.</strong> A hostname no
 * channel claims produces a neutral line naming no restaurant — not a default
 * site, not the first tenant, not a redirect.
 */
export function SiteStateNotice({
  state,
  t,
}: {
  state: RestaurantSiteState;
  t: (key: string, params?: Record<string, string | number>) => string;
}): ReactNode {
  switch (state.status) {
    case 'loading':
      return <EmptyNote>{t('hospitality.loading')}</EmptyNote>;
    case 'no-source':
      return <EmptyNote>{t('hospitality.notConnected')}</EmptyNote>;
    case 'missing':
      return <EmptyNote>{t('hospitality.siteMissing')}</EmptyNote>;
    case 'error':
      return <EmptyNote>{t('hospitality.error')}</EmptyNote>;
    default:
      return null;
  }
}

/**
 * `HH:mm` from whatever the backend wrote.
 *
 * <p>`SiteHoursDto.opensAt` is a `LocalTime` rendered as a string, so it may
 * arrive as `12:00`, `12:00:00` or `12:00:00.000`. It is trimmed, never
 * re-formatted: there is no date and no zone to hang an `Intl` format on, and
 * inventing one would put the server's timezone into a value that belongs to
 * the channel's.
 */
export function clockTime(value: string | null): string | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!match) return value.trim();
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

/**
 * An ISO local date (`2026-01-01`) as words in the visitor's locale.
 *
 * <p>Formatted in UTC deliberately: the value is a calendar date with no
 * instant behind it, and running it through a real zone is what turns "1
 * January" into "31 December" for half the world.
 */
export function localDateLabel(value: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(`${value}T00:00:00Z`));
  } catch {
    return value;
  }
}
